import React, { useEffect, useRef, useState } from "react"
import * as Yup from "yup"
import { useQueryClient } from "react-query"
import { Form, Formik, FormikHelpers } from "formik"
import { toast } from "react-toastify"
import { AxiosError } from "axios"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"
import { IHttpErrorResponseModel, IPersonnelNdfDetailModel, IPersonnelNdfDocumentModel } from "@common-models/*"
import { CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR, CONST_MONTANT_INDEMNITE_PAR_KM, CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM } from "@common-constants/*"
import MyAlert from "@common-utils/MyAlert"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyInputFormField from "@common-components/fields/MyInputFormField"

// Sous-composants
import { NdfBasicFieldsSection } from "./components/NdfBasicFieldsSection"
import { NdfMealSection } from "./components/NdfMealSection"
import { NdfMileageSection } from "./components/NdfMileageSection"
import { NdfCommentSection } from "./components/NdfCommentSection"
import { NdfJustificatifsEditSection } from "./components/NdfJustificatifsEditSection"
import {
     addPersonnelNdfJustificatifRequest,
     deletePersonnelNdfJustificatifRequest,
     editPersonnelNdfDepenseSimpleRequest,
     replacePersonnelNdfJustificatifRequest,
} from "../../../core/_requests"
import MyGoogleDriveFileViewerInModal from "../../../../../../../components/MyDocumentViewer/MyGoogleDriveFileViewerInModal"
import { IEditConnectedUserNdfDepenseSimpleRequestModel } from "../../../../../mes-activites/ndf/core/_models"
import { useConnectedUserNdfDepensesContext } from "../../_PersonnelNdfDepenses"

interface IConnectedUserNdfDepenseSimpleEditProps {
     detail: IPersonnelNdfDetailModel
}

const ConnectedUserNdfDepenseSimpleEdit = ({ detail }: IConnectedUserNdfDepenseSimpleEditProps) => {
     const queryClient = useQueryClient()
     const context = useConnectedUserNdfDepensesContext()

     // --- NOUVEAUX ÉTATS POUR LE LIVE EDIT DES DOCUMENTS ---
     const [localDocuments, setLocalDocuments] = useState<IPersonnelNdfDocumentModel[]>(detail.personnelNdfDocuments || [])
     const [isLoadingDocAction, setIsLoadingDocAction] = useState(false)

     const [docToBeReplaced, setDocToBeReplaced] = useState<IPersonnelNdfDocumentModel>()
     const refReplaceDocInput = useRef<HTMLInputElement>(null)
     const refAddDocInput = useRef<HTMLInputElement>(null)
     const MySwal = withReactContent(Swal)
     const [justificatifInModal, setJustificatifInModal] = useState<IPersonnelNdfDocumentModel>()

     const [detailsIndemniteKm, setDetailsIndemniteKm] = useState({
          distanceEnKmEntreDepartEtDestination: 0,
          distanceTotal: 0,
          montantTTC: 0,
     })

     const validationSchema = Yup.object().shape({
          categorie: Yup.string().label("Catégorie").required("Champ requis"),
          date: Yup.string()
               .label("Date")
               .when("categorie", {
                    is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.notRequired(),
                    otherwise: schema => schema.required("Champ requis"),
               }),
          montantTTC: Yup.number()
               .label("Montant TTC")
               .when("categorie", {
                    is: (val: string) => val !== CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required(),
               }),
          moisAnneeKm: Yup.string()
               .label("Mois du trajet")
               .when("categorie", {
                    is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required("Sélectionnez le mois"),
               }),
          joursKm: Yup.array()
               .label("Jours sélectionnés")
               .when("categorie", {
                    is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                    then: schema =>
                         schema.min(1, "Sélectionnez au moins un jour").test("exact", "Sélectionnez autant de jours que de trajets", function (val) {
                              return val?.length === this.parent.nbTrajets
                         }),
               }),
          description: Yup.string()
               .label("Description")
               .when("categorie", {
                    is: (val: string) => val !== CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required("Champ requis"),
               }),
     })

     const initialValues: IEditConnectedUserNdfDepenseSimpleRequestModel = {
          date: detail.date?.format("YYYY-MM-DD") || "",
          categorie: detail.categorie as string,
          montantTTC: detail.montantTtc ? Number(detail.montantTtc.toFixed(2)) : 0,
          description: detail.description || "",
          repasSeulOuEnGroupe: detail.invites?.length ? "REPAS_MIDI_EN_GROUPE" : "REPAS_MIDI_SEUL",
          invites: detail.invites || [],
          adresseDepart: detail.indemniteKmDetails?.addresseDepart || "",
          adresseArrive: detail.indemniteKmDetails?.addresseArrive || "",
          nbTrajets: detail.indemniteKmDetails?.nbTrajets || 0,
          isAllerRetour: detail.indemniteKmDetails?.isAllerRetour || false,
          distanceTotale: detail.indemniteKmDetails?.distanceKmTotal || 0,
          moisAnneeKm: detail.date?.format("YYYY-MM") || "",
          joursKm: detail.indemniteKmDetails?.joursKm || [],
     }

     useEffect(() => {
          if (detail.indemniteKmDetails) {
               setDetailsIndemniteKm({
                    distanceEnKmEntreDepartEtDestination: detail.indemniteKmDetails.distanceKmEntreDepartEtArrive || 0,
                    distanceTotal: detail.indemniteKmDetails.distanceKmTotal || 0,
                    montantTTC: Number(((detail.indemniteKmDetails.distanceKmTotal || 0) * CONST_MONTANT_INDEMNITE_PAR_KM).toFixed(2)),
               })
          }
     }, [detail])

     const handleEditSubmit = (values: IEditConnectedUserNdfDepenseSimpleRequestModel, helpers: FormikHelpers<IEditConnectedUserNdfDepenseSimpleRequestModel>) => {
          helpers.setSubmitting(true)
          helpers.setStatus(null)
          editPersonnelNdfDepenseSimpleRequest(detail.id!, values)
               .then(() => {
                    queryClient.invalidateQueries([context.REACT_QUERY_KEY_GET_LISTING, context.ndfId])
                    toast.success("Modifications enregistrées.")
                    context.setCurrentDepenseInEdition(undefined)
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key as any, error.errors[key])
                    }
                    helpers.setStatus(error?.detail)
                    helpers.setSubmitting(false)
               })
     }

     // --- HANDLERS DOCUMENTS (Mise à jour locale et Loaders) ---
     const onAddFile = (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0]
          if (file) {
               setIsLoadingDocAction(true)
               addPersonnelNdfJustificatifRequest(detail.id!, file)
                    .then(r => {
                         setLocalDocuments(prev => [...prev, r.data]) // Update local
                         queryClient.invalidateQueries([context.REACT_QUERY_KEY_GET_LISTING, context.ndfId])
                         toast.success("Document ajouté")
                    })
                    .finally(() => {
                         setIsLoadingDocAction(false)
                         if (refAddDocInput.current) refAddDocInput.current.value = "" // Reset input
                    })
          }
     }

     const onReplaceFile = (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0]
          if (file && docToBeReplaced) {
               setIsLoadingDocAction(true)
               replacePersonnelNdfJustificatifRequest(docToBeReplaced.id!, file)
                    .then(r => {
                         // Update local: on remplace l'ancien doc par le nouveau retourné par l'API
                         setLocalDocuments(prev => prev.map(doc => (doc.id === docToBeReplaced.id ? r.data : doc)))
                         queryClient.invalidateQueries([context.REACT_QUERY_KEY_GET_LISTING, context.ndfId])
                         toast.success("Document remplacé")
                    })
                    .finally(() => {
                         setIsLoadingDocAction(false)
                         setDocToBeReplaced(undefined)
                         if (refReplaceDocInput.current) refReplaceDocInput.current.value = "" // Reset input
                    })
          }
     }

     const handleRemoveDoc = (id: number, idx: number) => {
          MySwal.fire({
               title: `Supprimer le n°${idx}?`,
               icon: "warning",
               showCancelButton: true,
               confirmButtonText: "Oui, supprimer",
               cancelButtonText: "Annuler",
          }).then(r => {
               if (r.isConfirmed) {
                    setIsLoadingDocAction(true)
                    deletePersonnelNdfJustificatifRequest(id)
                         .then(() => {
                              setLocalDocuments(prev => prev.filter(doc => doc.id !== id)) // Update local
                              queryClient.invalidateQueries([context.REACT_QUERY_KEY_GET_LISTING, context.ndfId])
                              toast.success("Document supprimé")
                         })
                         .finally(() => setIsLoadingDocAction(false))
               }
          })
     }

     return (
          <Formik initialValues={initialValues} onSubmit={handleEditSubmit} validationSchema={validationSchema}>
               {helpers => {
                    const isKm = helpers.values.categorie === CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM
                    const showCommentAndSubmit = !isKm || (isKm && !!helpers.values.moisAnneeKm)

                    return (
                         <Form noValidate>
                              {helpers.status && (
                                   <MyAlert type="danger" classNames="mb-4">
                                        {helpers.status}
                                   </MyAlert>
                              )}

                              <div className="row g-9">
                                   <div className={`col-lg-7 col-xl-8 order-2 order-lg-1 ${!helpers.values.categorie ? "mx-auto" : ""}`}>
                                        <div className="d-flex flex-column gap-4">
                                             <NdfBasicFieldsSection helpers={helpers} validationSchema={validationSchema} />

                                             {helpers.values.categorie && (
                                                  <div className="animate__animated animate__fadeIn">
                                                       <div className="my-4 separator separator-dashed" />
                                                       <NdfMealSection helpers={helpers} validationSchema={validationSchema} />

                                                       {!isKm && (
                                                            <MyInputFormField
                                                                 formikProps={helpers}
                                                                 validationSchema={validationSchema}
                                                                 name="montantTTC"
                                                                 label={<div className="required">Montant TTC</div>}
                                                                 type="number"
                                                                 placeholder="Saisissez le montant TTC"
                                                                 classNameLabel="col-lg-4"
                                                                 classNameField="col-lg-8"
                                                            />
                                                       )}

                                                       <NdfMileageSection
                                                            helpers={helpers}
                                                            validationSchema={validationSchema}
                                                            detailsIndemniteKm={detailsIndemniteKm}
                                                            setDetailsIndemniteKm={setDetailsIndemniteKm}
                                                       />

                                                       {showCommentAndSubmit && (
                                                            <div className="animate__animated animate__fadeIn">
                                                                 <NdfCommentSection helpers={helpers} validationSchema={validationSchema} />
                                                                 <div className="separator separator-dashed my-6" />
                                                                 <div className="text-center d-flex justify-content-end">
                                                                      <button className="btn btn-sm btn-primary" type="submit" disabled={helpers.isSubmitting}>
                                                                           {!helpers.isSubmitting ? (
                                                                                <>
                                                                                     Enregistrer <i className="fas fa-check-circle ms-2" />
                                                                                </>
                                                                           ) : (
                                                                                <MySimpleSpinner size="sm" />
                                                                           )}
                                                                      </button>
                                                                 </div>
                                                            </div>
                                                       )}
                                                  </div>
                                             )}
                                        </div>
                                   </div>

                                   {helpers.values.categorie && (
                                        <div className="col-lg-5 col-xl-4 order-1 order-lg-2 mb-8 mb-lg-0 animate__animated animate__fadeInRight">
                                             <div className="position-sticky" style={{ top: "25px" }}>
                                                  <NdfJustificatifsEditSection
                                                       documents={localDocuments} // On passe l'état local !
                                                       isLoading={isLoadingDocAction} // On passe l'état de chargement
                                                       onAddClick={() => refAddDocInput.current?.click()}
                                                       onReplaceClick={doc => {
                                                            setDocToBeReplaced(doc)
                                                            refReplaceDocInput.current?.click()
                                                       }}
                                                       onRemoveClick={handleRemoveDoc}
                                                       onViewClick={setJustificatifInModal}
                                                       isKm={isKm}
                                                  />
                                             </div>
                                        </div>
                                   )}
                              </div>

                              {/* Inputs resetés après chaque action pour permettre de ré-uploader le même fichier */}
                              <input type="file" className="d-none" ref={refAddDocInput} onChange={onAddFile} />
                              <input type="file" className="d-none" ref={refReplaceDocInput} onChange={onReplaceFile} />

                              {justificatifInModal && (
                                   <MyGoogleDriveFileViewerInModal
                                        googleDriveID={justificatifInModal.googleDriveID!}
                                        googleDriveMimetype={justificatifInModal.googleDriveMimeType!}
                                        modalTitle={<span>Justificatif</span>}
                                        handleCloseModal={() => setJustificatifInModal(undefined)}
                                   />
                              )}
                         </Form>
                    )
               }}
          </Formik>
     )
}

export default ConnectedUserNdfDepenseSimpleEdit
